import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { websocketService, ConnectionState } from '../services/websocketService';
import logger from '../utils/debugLogger';

interface WebSocketContextType {
  isConnected: boolean;
  connecting: boolean;
  connectionState: ConnectionState;
  connect: () => Promise<void>;
  disconnect: () => void;
  error: Error | null;
}

// Create context with a default undefined value
const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

/**
 * Provider component that wraps the application and makes WebSocket state available to any
 * child component that calls useWebSocket().
 */
export const WebSocketProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  // Initialize state from the websocketService
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    websocketService.getConnectionState()
  );

  // Connect to the WebSocket
  const connect = useCallback(async () => {
    try {
      logger.info('WebSocketContext: Initiating connection');
      await websocketService.connect();
      logger.info('WebSocketContext: Connection successful');
    } catch (error) {
      logger.error('WebSocketContext: Connection failed:', error);
      // State will be updated via the connection state handler
    }
  }, []);

  // Disconnect from the WebSocket
  const disconnect = useCallback(() => {
    logger.info('WebSocketContext: Disconnecting');
    websocketService.disconnect();
  }, []);

  // Subscribe to connection state changes
  // Log environment info on initial render
  useEffect(() => {
    logger.logEnvironmentInfo();
  }, []);

  useEffect(() => {
    logger.info('WebSocketProvider: Setting up connection state listener');
    
    const unsubscribe = websocketService.onConnectionStateChange((state) => {
      logger.info('WebSocketProvider: Connection state changed:', state);
      setConnectionState(state);
    });
    
    // Attempt to connect if not already connected
    if (!connectionState.connected && !connectionState.connecting) {
      logger.info('WebSocketProvider: Attempting initial connection');
      connect().catch(err => 
        logger.error('WebSocketProvider: Initial connection failed:', err)
      );
    }
    
    // Set up ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (connectionState.connected) {
        logger.debug('WebSocketProvider: Sending ping to keep connection alive');
        websocketService.send('ping', {}).catch(err => {
          logger.error('WebSocketProvider: Ping failed, connection may be lost:', err);
          // If ping fails, attempt to reconnect
          if (connectionState.connected) {
            logger.info('WebSocketProvider: Attempting to reconnect after failed ping');
            connect().catch(err => 
              logger.error('WebSocketProvider: Reconnection failed:', err)
            );
          }
        });
      }
    }, 45000); // Ping every 45 seconds
    
    // Cleanup subscription and interval on unmount
    return () => {
      logger.info('WebSocketProvider: Cleaning up connection state listener and ping interval');
      unsubscribe();
      clearInterval(pingInterval);
    };
  }, [connect, connectionState.connected, connectionState.connecting]);

  // Create the context value object
  const contextValue: WebSocketContextType = {
    isConnected: connectionState.connected,
    connecting: connectionState.connecting,
    connectionState,
    connect,
    disconnect,
    error: connectionState.error
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook that lets any component easily access the WebSocket context.
 * Throws an error if used outside of a WebSocketProvider.
 */
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};